import { ReportElementData } from "@/shared/types/element";
import { useTheme } from "@mui/material/styles";
import {
  PaxElementWrapper,
  PaxElementWrapperProps,
  PaxErrorPage,
  PaxTable,
  PaxTableProps,
} from "@ncino/pax-ui";
import { useState } from "react";
import { resolveElementData } from "./util";
import {
  Report_ElementQueryOptionsInput,
  Report_ElementSettingsInput,
  Report_ElementStatus,
  Report_ElementType,
} from "@/services/graphql/reactQuery";

/* Example usage of the PaxTableElement:
    <PaxTableElement
      sx={{ maxWidth: "90vw", maxHeight: "50vh" }}
      paragraph="Lorem ipsum dolor sit amet consectetur adipisicing elit. Adipisci cumque voluptates nesciunt ipsa iste dolorem beatae eveniet laboriosam officiis sapiente, officia quia facilis voluptatem. Deleniti impedit pariatur itaque quaerat aspernatur."
      note="this is a test note"
      elementData={getTableMockData()}
      title={"Test List"}
      striped
      onMoreOptions={() => window.alert("clicked on more options")}
      onEdit={() => window.alert("clicked on on edit")}
    />
*/

interface PaxTableElementBaseProps {
  elementData: ReportElementData;
  settings?: Report_ElementQueryOptionsInput;
  tableHeaderVariant?: "dark" | "light";
}

export type PaxTableElementProps = PaxTableElementBaseProps &
  Omit<PaxElementWrapperProps, "children"> &
  Omit<PaxTableProps, "children" | "pagination"> & { isLoading?: boolean };

export default function PaxTableElement({
  // element wrapper props
  title = "Table",
  showHeader,
  paragraph,
  note,
  sx, // by convention, sx applies to the outer-most element
  isLoading,
  onMoreOptions,
  onEdit,
  elevated,
  customHeader,
  // the rest are pax table props
  ...paxTableProps
}: PaxTableElementProps) {
  const theme = useTheme();
  const [sortColumnIndex, setSortColumnIndex] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const elementWrapperProps = {
    title,
    showHeader,
    paragraph,
    note,
    onMoreOptions,
    onEdit,
    elevated,
    sx,
    customHeader,
  };

  function handleSortChange(colIndex: number) {
    if (sortColumnIndex === colIndex) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumnIndex(colIndex);
      setSortDirection("asc");
    }
  }

  const sortConfig = { sortColumnIndex, sortDirection, onSortChange: handleSortChange };

  // if more than one strat, we must use custom striping due to subheaders
  let hasTwoPlusStrats = false;
  if (paxTableProps.elementData.numStrats != null) {
    hasTwoPlusStrats = paxTableProps.elementData.numStrats > 1 ? true : false;
  }

  paxTableProps.stripeColor = paxTableProps.stripeColor
    ? paxTableProps.stripeColor
    : theme.palette.info.selected;

  const isError = paxTableProps.elementData.status === Report_ElementStatus.Failed;
  const isProcessing =
    paxTableProps.elementData.status === Report_ElementStatus.Processing ||
    paxTableProps.elementData.status === Report_ElementStatus.Queued;
  const isLoadingOrProcessing = isLoading || isProcessing;

  const isEmpty =
    paxTableProps.isEmpty ||
    (!isLoadingOrProcessing &&
      !isError &&
      ((paxTableProps.elementData.tableData ?? null)?.rows?.length ?? 0) === 0);

  // TODO: need to handle error / isprocessing in other ticket
  if (isError) {
    return (
      <PaxElementWrapper {...elementWrapperProps}>
        <p>Error: {paxTableProps.elementData.error ?? "Something went wrong."}</p>
      </PaxElementWrapper>
    );
  }

  return (
    <PaxElementWrapper {...elementWrapperProps}>
      {/* table */}
      <PaxTable
        {...paxTableProps}
        isEmpty={isEmpty}
        striped={paxTableProps.striped && !hasTwoPlusStrats}
        stripeColor={paxTableProps.stripeColor}
        sx={{
          "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
            borderBottom: "none",
          },
        }}
      >
        {resolveElementData(
          Report_ElementType.Table,
          paxTableProps.elementData,
          paxTableProps,
          theme,
          sortConfig,
          isLoadingOrProcessing
        )}
      </PaxTable>
    </PaxElementWrapper>
  );
}
